// src/App.js - App principale completo
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useOfflineStorage } from './hooks/useOfflineStorage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import Layout from './components/layout/Layout';
import LoginForm from './components/auth/LoginForm';
import OrderWizard from './components/orders/OrderWizard';
import OrderList from './components/orders/OrderList';
import OfflineOrdersPage from './pages/OfflineOrdersPage';
import CustomersPage from './pages/CustomersPage';
import SuperAdminPage from './pages/SuperAdminPage';
import InstallBanner from './components/pwa/InstallBanner';

// Funzione per preload di prodotti e clienti per funzionalità offline
const preloadDataForOffline = async (ApiService, offlineStorage) => {
  try {    
    // Controlla se i dati sono già stati precaricati di recente
    const cachedProducts = offlineStorage.getCachedProducts();
    const cachedCustomers = offlineStorage.getCachedCustomers();
        
    // Determina se dobbiamo ricaricare i dati - solo quando online
    const shouldUpdate = offlineStorage.shouldUpdateCache();
    const shouldReloadProducts = cachedProducts.length === 0 || shouldUpdate;
    const shouldReloadCustomers = cachedCustomers.length === 0 || shouldUpdate;
    
    // Preload prodotti se cache vuota - sempre cerca di caricare tutti i prodotti
    if (shouldReloadProducts) {
      try {
        const products = await ApiService.getAllProducts();
        
        if (products && Array.isArray(products) && products.length > 0) {
          offlineStorage.cacheProducts(products);
        } else {
          console.warn('⚠️ Nessun prodotto ricevuto da getAllProducts, provo con getProducts...');
          // Prova con metodo standard se getAllProducts fallisce
          const fallbackProducts = await ApiService.getProducts();
          if (fallbackProducts && Array.isArray(fallbackProducts)) {
            offlineStorage.cacheProducts(fallbackProducts);
          } else {
            console.error('❌ Impossibile caricare prodotti con entrambi i metodi');
          }
        }
      } catch (error) {
        console.error('❌ Errore preload prodotti dal backend:', error.message);
        console.error('Stack trace:', error);
      }
    } 
    
    // Preload clienti se cache vuota - sempre cerca di caricare tutti i clienti  
    if (shouldReloadCustomers) {
      try {
        const customers = await ApiService.getAllCustomers();
        
        if (customers && Array.isArray(customers) && customers.length > 0) {
          offlineStorage.cacheCustomers(customers);
        } else {
          console.warn('⚠️ Nessun cliente ricevuto da getAllCustomers, provo con getCustomers...');
          const fallbackCustomers = await ApiService.getCustomers();
          if (fallbackCustomers && Array.isArray(fallbackCustomers)) {
            offlineStorage.cacheCustomers(fallbackCustomers);
          } else {
            console.error('❌ Impossibile caricare clienti con entrambi i metodi');
          }
        }
      } catch (error) {
        console.error('❌ Errore preload clienti dal backend:', error.message);
        console.error('Stack trace:', error);
      }
    } 
    
  } catch (error) {
    console.error('❌ Errore durante preload dati:', error);
  }
};

// Componente per inizializzare servizi offline
const OfflineProvider = ({ children }) => {
  const offlineStorage = useOfflineStorage();
  const { user, loading } = useAuth();

  useEffect(() => {
    // Configura ApiService con gestione offline se disponibile
    const configureApiService = async () => {
      try {
        const { default: ApiService } = await import('./services/api');
        
        // Verifica che ApiService abbia il metodo setOfflineStorage
        if (ApiService && typeof ApiService.setOfflineStorage === 'function') {
          ApiService.setOfflineStorage(offlineStorage);
        } else {
          console.warn('⚠️ ApiService.setOfflineStorage non disponibile');
        }
        
        // Sincronizza ordini pendenti al caricamento se online
        if (offlineStorage.isOnline && offlineStorage.pendingSyncCount > 0) {
          await offlineStorage.syncPendingOrders();
        }

        // Sincronizza operazioni clienti pendenti se online
        if (offlineStorage.isOnline) {
          const pendingCustomerOps = offlineStorage.getOfflineCustomerOperations();
          if (pendingCustomerOps.length > 0) {
            try {
              await offlineStorage.syncOfflineCustomers();
              offlineStorage.clearSyncedCustomerOperations();
            } catch (error) {
              console.warn('⚠️ Errore sincronizzazione automatica clienti:', error);
            }
          }
        }

        // La cache non scade più automaticamente - persiste offline

        // Preload prodotti e clienti per funzionalità offline - SOLO se utente autenticato
        if (offlineStorage.isOnline && user && !loading) {
          // Usa setTimeout per evitare di bloccare l'inizializzazione dell'app
          setTimeout(async () => {
            try {
              await preloadDataForOffline(ApiService, offlineStorage);
            } catch (error) {
              console.error('❌ Errore durante preload asincrono:', error);
              // Riprova dopo 5 secondi se il preload fallisce
              setTimeout(() => {
                if (offlineStorage.isOnline && user && !loading) {
                  preloadDataForOffline(ApiService, offlineStorage);
                }
              }, 5000);
            }
          }, 1000); // Attendi 1 secondo prima di iniziare il preload
        }
      } catch (error) {
        console.error('❌ Errore configurazione ApiService:', error.message);
        
        // Non utilizzare dati mock - solo backend reale
        console.warn('⚠️ ApiService non disponibile. Assicurati che il backend sia raggiungibile.');
      }
    };

    configureApiService();
  }, [offlineStorage, user, loading]);

  // Effetto per ri-preload quando torna online
  useEffect(() => {
    const handleOnlinePreload = async () => {
      if (offlineStorage.isOnline && user && !loading) {
        
        // Controlla se la cache va aggiornata (solo quando online)
        const shouldUpdate = offlineStorage.shouldUpdateCache();
        
        // Se la cache va aggiornata, riprova il preload
        if (shouldUpdate) {
          setTimeout(async () => {
            try {
              const { default: ApiService } = await import('./services/api');
              await preloadDataForOffline(ApiService, offlineStorage);
            } catch (error) {
              console.error('❌ Errore preload post-connessione:', error);
            }
          }, 2000);
        }
      }
    };

    handleOnlinePreload();
  }, [offlineStorage.isOnline, user, loading]);

  return children;
};

// Componente Coming Soon per funzionalità future
const ComingSoon = ({ title }) => (
  <div className="flex items-center justify-center min-h-screen bg-white">
    <div className="text-center max-w-md mx-auto px-6">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <span className="text-2xl">🚧</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
      <p className="text-gray-600 mb-6">Questa funzionalità sarà disponibile prossimamente</p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 font-medium mb-2">
          Funzionalità pianificate:
        </p>
        <ul className="text-sm text-blue-700 space-y-1 text-left">
          {title === 'Prodotti' && (
            <>
              <li>• Gestione catalogo prodotti</li>
              <li>• Modifica prezzi e disponibilità</li>
              <li>• Creazione nuovi articoli</li>
              <li>• Import/export Excel</li>
            </>
          )}
          {title === 'Statistiche' && (
            <>
              <li>• Dashboard vendite agente</li>
              <li>• Analisi performance mensili</li>
              <li>• Report clienti e prodotti</li>
              <li>• Grafici e KPI</li>
            </>
          )}
          {title === 'Clienti' && (
            <>
              <li>• Anagrafica clienti completa</li>
              <li>• Storico ordini cliente</li>
              <li>• Note e comunicazioni</li>
              <li>• Export dati</li>
            </>
          )}
        </ul>
      </div>
    </div>
  </div>
);

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <OfflineProvider>
          <div className="App">
            <Routes>
              {/* Rotta pubblica per il login */}
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <LoginForm />
                  </PublicRoute>
                } 
              />
              
              {/* Rotte protette con Layout */}
              <Route 
                path="/*" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        {/* Home - Nuovo Ordine */}
                        <Route path="/" element={<OrderWizard />} />
                        
                        {/* Lista ordini */}
                        <Route path="/orders" element={<OrderList />} />
                        
                        {/* Ordini offline */}
                        <Route path="/offline-orders" element={<OfflineOrdersPage />} />
                        
                        {/* Funzionalità future - Coming Soon */}
                        <Route 
                          path="/products" 
                          element={<ComingSoon title="Prodotti" />} 
                        />
                        
                        <Route 
                          path="/customers" 
                          element={<CustomersPage />} 
                        />

                        <Route 
                          path="/admin" 
                          element={<SuperAdminPage />} 
                        />
                        
                        <Route 
                          path="/stats" 
                          element={<ComingSoon title="Statistiche" />} 
                        />
                        
                        {/* Redirect per rotte non trovate */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      
                      {/* Banner installazione PWA */}
                      <InstallBanner />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
            </Routes>
          </div>
        </OfflineProvider>
      </Router>
    </AuthProvider>
  );
};

export default App;